import { create } from 'zustand'
import { useQueryClient } from '@tanstack/react-query'
import { qk } from '../hooks/queryKeys'
import { saveAppState } from './db'
import { toastOk } from './toastStore'
import type { AppState } from '../types/firestore'

const MAX = 30

interface UndoState {
  undoStack: string[]
  redoStack: string[]
  push: (state: AppState) => void
  canUndo: () => boolean
  canRedo: () => boolean
}

export const useUndoStore = create<UndoState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  push: (state) =>
    set((s) => {
      const stack = [...s.undoStack, JSON.stringify(state)]
      if (stack.length > MAX) stack.shift()
      return { undoStack: stack, redoStack: [] }
    }),
  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,
}))

export function useUndoRedo(uid: string | null | undefined) {
  const qc = useQueryClient()
  const store = useUndoStore()

  async function undo() {
    if (!uid || !store.canUndo()) return
    const { undoStack, redoStack } = useUndoStore.getState()
    const current = qc.getQueryData<AppState>(qk.appstate(uid))
    const snapshot = undoStack[undoStack.length - 1]
    if (!snapshot) return
    const restored = JSON.parse(snapshot) as AppState
    useUndoStore.setState({
      undoStack: undoStack.slice(0, -1),
      redoStack: current ? [...redoStack, JSON.stringify(current)].slice(-MAX) : redoStack,
    })
    qc.setQueryData(qk.appstate(uid), restored)
    await saveAppState(uid, restored)
    toastOk('Undone')
  }

  async function redo() {
    if (!uid || !store.canRedo()) return
    const { undoStack, redoStack } = useUndoStore.getState()
    const current = qc.getQueryData<AppState>(qk.appstate(uid))
    const snapshot = redoStack[redoStack.length - 1]
    if (!snapshot) return
    const restored = JSON.parse(snapshot) as AppState
    useUndoStore.setState({
      redoStack: redoStack.slice(0, -1),
      undoStack: current ? [...undoStack, JSON.stringify(current)].slice(-MAX) : undoStack,
    })
    qc.setQueryData(qk.appstate(uid), restored)
    await saveAppState(uid, restored)
    toastOk('Redone')
  }

  return { undo, redo, canUndo: store.canUndo(), canRedo: store.canRedo() }
}
